import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { fmtDateTime } from '../lib/util';
import { DeadlineIndicator } from '../components/DeadlineIndicator';
import { getCurrentUser } from '../auth/useUser';
import { useAppData } from '../state/contexts/AppDataContext';
import { useUIState } from '../state/contexts/UIStateContext';
import { useLifecycleActions } from '../state/actions/lifecycle';
import { useAdminActions } from '../state/actions/admin';

export function Legal() {
  const { cases, now } = useAppData();
  const { setModal } = useUIState();
  const { confirmDeletion } = useLifecycleActions();
  const { confirmBcidNotification } = useAdminActions();
  const user = getCurrentUser()!;
  const canAdmin = user.canAdmin;

  const allLp = cases.flatMap(c => (c.legalProcessNotifications || []).map((lp: any) => ({ ...lp, caseId: c.id, registrant: c.registrant })));

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 4 }}>Legal & Compliance</h2>
      <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 20 }}>Legal process tracking · BCID LLC notification (24hr) · Pre-audit assessment · Data sharing restrictions</div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <div style={styles.card}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: C.goldText }}>Legal Process Notifications</h3>
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>BCID LLC must be notified in writing within 24 hours of receipt of legal process or enforcement inquiry</div>
          <button style={{ ...styles.btn, ...styles.btnGold, marginBottom: 16 }} onClick={() => setModal('log-legal-process')}>+ Log Legal Process</button>
          {allLp.length > 0 ? (
            <table style={styles.table}>
              <thead><tr>
                <th style={styles.th}>Case</th><th style={styles.th}>Description</th><th style={styles.th}>Received</th><th style={styles.th}>BCID Notified</th><th style={styles.th}>Action</th>
              </tr></thead>
              <tbody>
                {allLp.map((lp: any) => (
                  <tr key={lp.id}>
                    <td style={styles.td}>{lp.registrant} ({lp.caseId})</td>
                    <td style={styles.td}>{lp.description}</td>
                    <td style={styles.td}>{fmtDateTime(lp.loggedAt)}</td>
                    <td style={styles.td}>{lp.bcidNotifiedAt ? <span style={{ color: C.success }}>✓ {fmtDateTime(lp.bcidNotifiedAt)}</span> : <DeadlineIndicator deadline={lp.notifyDeadline} label="24hr deadline" now={now} />}</td>
                    <td style={styles.td}>{!lp.bcidNotifiedAt && <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={() => confirmBcidNotification(lp.caseId, lp.id)}>Confirm Sent</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div style={{ color: C.textMuted, fontSize: 13 }}>No legal process records</div>}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={styles.card}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: C.goldText }}>Pre-Audit Assessment</h3>
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>Participate in Pre-Audit Assessment upon BCID LLC request prior to commercial launch</div>
          <div style={styles.checkItem}><span style={{ color: C.success }}>✓</span> Vetting procedures documented (effective 3/1/2026)</div>
          <div style={styles.checkItem}><span style={{ color: C.success }}>✓</span> Dual-control decision workflow implemented</div>
          <div style={styles.checkItem}><span style={{ color: C.success }}>✓</span> Immutable audit trail active</div>
          <div style={styles.checkItem}><span style={{ color: C.success }}>✓</span> Role-based access control configured</div>
          <div style={styles.checkItem}><span style={{ color: C.success }}>✓</span> MFA enabled for all vetting staff</div>
          <div style={styles.checkItem}><span style={{ color: C.success }}>✓</span> Settlement Platform integration configured</div>
          <div style={styles.checkItem}><span style={{ color: C.success }}>✓</span> Notification & deadline tracking active</div>
          <button style={{ ...styles.btn, ...styles.btnOutline, marginTop: 16, width: '100%' }} onClick={() => setModal('audit-report')}>Generate Audit Readiness Report</button>
        </div>
        <div style={styles.card}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: C.goldText }}>Data Sharing Restrictions</h3>
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>Permitted data sharing per Section 3(g)</div>
          <div style={{ fontSize: 12, lineHeight: 1.8 }}>
            <div><span style={{ color: C.success }}>✓</span> Onboarding Agent</div>
            <div><span style={{ color: C.success }}>✓</span> Settlement Platform / TSP</div>
            <div><span style={{ color: C.success }}>✓</span> BCID LLC / Audit Organization</div>
            <div><span style={{ color: C.success }}>✓</span> Data Sources (verification vendors)</div>
            <div><span style={{ color: C.warn }}>⚠</span> Lawfully served legal process (notify BCID LLC within 24hr)</div>
            <div><span style={{ color: C.warn }}>⚠</span> Federal/state/local enforcement inquiry (notify BCID LLC within 24hr)</div>
            <div style={{ marginTop: 8, color: C.danger }}>✗ All other third-party sharing strictly prohibited</div>
          </div>
        </div>
      </div>
      <div style={styles.card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: C.goldText }}>Account Deletion Requests</h3>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>Registrant or OA sends deletion request to support@brandedcallingid.com — removes TNs, display names, logos, and call reasons</div>
        {cases.filter(c => c.status === 'PENDING_DELETION').map(c => (
          <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
            <div>
              <strong>{c.registrant}</strong> ({c.id}) — Deletion requested
              <div style={{ fontSize: 12, color: C.textMuted }}>EID: {c.eid}</div>
            </div>
            <button style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSmall }} onClick={() => confirmDeletion(c.id)} disabled={!canAdmin}>{canAdmin ? 'Confirm Deletion' : 'Admin Required'}</button>
          </div>
        ))}
        {cases.filter(c => c.status === 'PENDING_DELETION').length === 0 && (
          <div style={{ color: C.textMuted, fontSize: 13 }}>No pending deletion requests</div>
        )}
      </div>
    </div>
  );
}
